/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import { getModelTypesList } from "data/resources/model-items-config";
import { Select, MenuItem, TextField } from "@mui/material";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import LabelledField from "components/labelled-field";
import { LinkSelectorWrapper } from "cms/links/link-selector/link-selector-wrapper";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";


// AddComponent: a simple button to trigger link selection
const AddLinkButton = ({ setOpen }) => (
    <button
        type="button"
        style={{
            margin: "8px 0",
            backgroundColor: "#078177",
            color: "#FFF",
            border: "none",
            borderRadius: "4px",
            padding: "8px 16px",
            cursor: "pointer",
            fontWeight: 500
        }}
        onClick={() => setOpen(true)}
    >
        Select Link
    </button>
);

const NewsAndEventsFeedComponentEditor = () => {

    const { component, update } = useUpdateComponent();
    const { token } = useAuth();
    
    const model_selected_type = component?.model_selected_type;
    const model_selected_type_2 = component?.model_selected_type_2;
    const model_selected_type_title = component?.model_selected_type_title;
    const model_selected_type_2_title = component?.model_selected_type_2_title;
    const newsLinkGuid = component?.news_link;
    const eventsLinkGuid = component?.events_link;

    const model_types = getModelTypesList();

    const model_types_event = model_types.filter((type) => {
        return type.type === "model.event";
    });

    const model_types_news = model_types.filter((type) => {
        return type.type === "model.news";
    });

    const onChangeType = (event) => {
        const selectedType = event.target.value;
        update("model_selected_type", selectedType);
    };

    const onChangeType2 = (event) => {
        const selectedType = event.target.value;
        update("model_selected_type_2", selectedType);
    };

    const onChangeTitle1 = (event) => {
        const model_selected_type_title = event.target.value;
        update("model_selected_type_title", model_selected_type_title);
    };

    const onChangeTitle2 = (event) => {
        const model_selected_type_2_title = event.target.value;
        update("model_selected_type_2_title", model_selected_type_2_title);
    };

    const [newsLink, setNewsLink] = useState();
    const [eventsLink, setEventsLink] = useState();
    const [loadingNewsLink, setLoadingNewsLink] = useState(false);
    const [loadingEventsLink, setLoadingEventsLink] = useState(false);

    // Load link object from guid if present
    useEffect(() => {
        const load = async () => {
            if (newsLinkGuid && typeof newsLinkGuid === "string" && newsLinkGuid.length > 20) {
                setLoadingNewsLink(true);
                try {
                    const linkObj = await SimpliCityAdminSDK.link.findOne(token, newsLinkGuid);
                    setNewsLink(linkObj);
                } catch (e) {
                    setNewsLink(undefined);
                } finally {
                    setLoadingNewsLink(false);
                }
            } else {
                setNewsLink(undefined);
            }
            if (eventsLinkGuid && typeof eventsLinkGuid === "string" && eventsLinkGuid.length > 20) {
                setLoadingEventsLink(true);
                try {
                    const linkObj = await SimpliCityAdminSDK.link.findOne(token, eventsLinkGuid);
                    setEventsLink(linkObj);
                } catch (e) {
                    setEventsLink(undefined);
                } finally {
                    setLoadingEventsLink(false);
                }
            } else {
                setEventsLink(undefined);
            }   
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newsLinkGuid, eventsLinkGuid, token]);

    const onChangeNewsLink = (link) => {
        setNewsLink(link);
        update("news_link", link?.guid);
    };
    const onRemoveNewsLink = () => {
        setNewsLink(undefined);
        update("news_link", undefined);
    };

    const onChangeEventLink = (link) => {
        setEventsLink(link);
        update("events_link", link?.guid);
    };
    const onRemoveEventLink = () => {
        setEventsLink(undefined);
        update("events_link", undefined);
    };

    return (
        <div className='simplicity-editor-frame'>
            <h2>First column: News feed</h2>
            <LabelledField label="Title">
                <TextField
                    onChange={onChangeTitle1}
                    id="model_selected_type_title"
                    defaultValue={model_selected_type_title}
                    // fullWidth={true}
                    sx={{ marginBottom: "0.5rem", width: "400px" }}
                />
            </LabelledField>
            <LabelledField label="Feed type">
                <Select
                    labelId={"category_label"}
                    id='model-select-1'
                    sx={{ width: "400px" }}
                    value={model_selected_type}
                    onChange={onChangeType}
                >
                    {model_types_news.map((type, index) => (
                        <MenuItem key={index} value={type.type}>{type.name}</MenuItem>
                    ))}
                </Select>
            </LabelledField>
            <div className='link-selector-container'>
                <LinkSelectorWrapper
                    link={newsLink}
                    setLink={setNewsLink}
                    onLinkChange={onChangeNewsLink}
                    onLinkRemove={onRemoveNewsLink}
                    disabled={false}
                    loadingLink={loadingNewsLink}
                    setLoadingLink={setLoadingNewsLink}
                    AddComponent={AddLinkButton}
                />
            </div>
            <h2>Second column: Events feed</h2>
            <LabelledField label="Title">
                <TextField
                    onChange={onChangeTitle2}
                    id="model_selected_type_2_title"
                    defaultValue={model_selected_type_2_title}
                    // fullWidth={true}
                    sx={{ marginBottom: "0.5rem", width: "400px" }}
                />
            </LabelledField>
            <LabelledField label="Feed type">
                <Select
                    labelId={"category_label"}
                    id='model-select-2'
                    sx={{ width: "400px" }}
                    value={model_selected_type_2}
                    onChange={onChangeType2}
                >
                    {model_types_event.map((type, index) => (
                        <MenuItem key={index} value={type.type}>{type.name}</MenuItem>
                    ))}
                </Select>
            </LabelledField>
            <div className='link-selector-container'>
                <LinkSelectorWrapper
                    link={eventsLink}
                    setLink={setEventsLink}
                    onLinkChange={onChangeEventLink}
                    onLinkRemove={onRemoveEventLink}
                    disabled={false}
                    loadingLink={loadingEventsLink}
                    setLoadingLink={setLoadingEventsLink}
                    AddComponent={AddLinkButton}
                />
            </div>
        </div>
    );
};

NewsAndEventsFeedComponentEditor.displayName = "NewsAndEventsFeedComponentEditor";
export { NewsAndEventsFeedComponentEditor };
